import { useCallback, useEffect, useRef, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Download, RefreshCw, Pencil, ChevronLeft, FileSpreadsheet, SlidersHorizontal } from 'lucide-react'
import { templateApi } from '@/api/services'
import { useToast } from '@/context/ToastContext'
import { Button } from '@/components/ui/Button'
import { Spinner } from '@/components/ui/misc'
import { ReportParametersModal } from '@/components/ReportParametersModal'
import type { MergedDataSourceInputParameter } from '@/types'
import {
  areRequiredParametersSatisfied,
  collectAutoResolvedParameterNames,
  fetchDataSourcesByKeys,
  hasOnlyOptionalParameters,
  mergeDataSourceInputParameters,
} from '@/lib/dataSourceParameters'
import { toastApiError } from '@/lib/apiError'

function isTemplateHtmlPresent(html: string | null | undefined): boolean {
  return (html ?? '').trim().length > 0
}

/** True when JSON exists and contains at least one Excel sheet definition. */
function hasUsableExcelConfig(excelConfigJson: string | null | undefined): boolean {
  if (excelConfigJson == null || String(excelConfigJson).trim() === '') return false
  try {
    const cfg = JSON.parse(excelConfigJson) as { sheets?: unknown[] }
    return Array.isArray(cfg.sheets) && cfg.sheets.length > 0
  } catch {
    return false
  }
}

export default function ViewerPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { toast } = useToast()

  const [html, setHtml] = useState<string>('')
  const [templateName, setTemplateName] = useState('')
  const [loading, setLoading] = useState(true)
  const [bootstrapLoading, setBootstrapLoading] = useState(true)
  const [exportingPdf, setExportingPdf] = useState(false)
  const [exportingXlsx, setExportingXlsx] = useState(false)
  const [parameters, setParameters] = useState<Record<string, string>>({})
  const [paramDefs, setParamDefs] = useState<MergedDataSourceInputParameter[]>([])
  const [autoResolvedDeps, setAutoResolvedDeps] = useState<Set<string>>(() => new Set())
  const [paramsModalOpen, setParamsModalOpen] = useState(false)
  /** After Apply in the modal, optional-only templates may proceed; required params still checked via values. */
  const [paramsModalAcknowledged, setParamsModalAcknowledged] = useState(false)
  const [canExportPdf, setCanExportPdf] = useState(false)
  const [canExportExcel, setCanExportExcel] = useState(false)
  /** Invoked with the record from the modal Apply so actions run with fresh values before React state catches up. */
  const pendingActionRef = useRef<((params: Record<string, string>) => void) | null>(null)

  const buildRenderPayload = useCallback(
    (paramOverride?: Record<string, string>) => {
      const p = paramOverride ?? parameters
      if (Object.keys(p).length === 0) return {}
      return { parameters: p }
    },
    [parameters]
  )

  const performRender = useCallback(
    async (paramOverride?: Record<string, string>) => {
      if (!id) return
      setLoading(true)
      try {
        const rendered = await templateApi.renderHtml(id, buildRenderPayload(paramOverride))
        setHtml(rendered)
      } catch (e: unknown) {
        toastApiError(toast, e, 'Failed to render report')
      } finally {
        setLoading(false)
      }
    },
    [id, buildRenderPayload, toast]
  )

  const performRenderRef = useRef(performRender)
  performRenderRef.current = performRender

  const shouldInterceptForParameters = useCallback(() => {
    if (paramDefs.length === 0) return false
    if (!areRequiredParametersSatisfied(paramDefs, parameters)) return true
    const hasRequired = paramDefs.some(d => d.isRequired)
    if (!hasRequired && !paramsModalAcknowledged) return true
    return false
  }, [paramDefs, parameters, paramsModalAcknowledged])

  const runAfterParametersOrNow = useCallback(
    (action: (params: Record<string, string>) => void | Promise<void>) => {
      if (shouldInterceptForParameters()) {
        pendingActionRef.current = params => {
          void action(params)
        }
        setParamsModalOpen(true)
        return
      }
      void action(parameters)
    },
    [shouldInterceptForParameters, parameters]
  )

  useEffect(() => {
    if (!id) return
    let cancelled = false
    ;(async () => {
      setBootstrapLoading(true)
      setParamDefs([])
      setAutoResolvedDeps(new Set())
      setParamsModalAcknowledged(false)
      setParameters({})
      setHtml('')
      setCanExportPdf(false)
      setCanExportExcel(false)
      try {
        const template = await templateApi.getById(id)
        if (cancelled) return
        setTemplateName(template.name)
        setCanExportPdf(isTemplateHtmlPresent(template.htmlContent))
        setCanExportExcel(hasUsableExcelConfig(template.excelConfigJson))
        const sources = await fetchDataSourcesByKeys(template.requiredDataSourceKeys ?? [])
        if (cancelled) return
        const defs = mergeDataSourceInputParameters(sources)
        setParamDefs(defs)
        setAutoResolvedDeps(collectAutoResolvedParameterNames(sources))

        if (defs.length === 0) {
          setParamsModalAcknowledged(true)
          setLoading(true)
          try {
            const rendered = await templateApi.renderHtml(id, {})
            if (cancelled) return
            setHtml(rendered)
          } catch (e: unknown) {
            if (!cancelled) toastApiError(toast, e, 'Failed to render report')
          } finally {
            if (!cancelled) setLoading(false)
          }
        } else {
          const hasRequired = defs.some(d => d.isRequired)
          const satisfied = areRequiredParametersSatisfied(defs, {})
          if (!hasRequired && satisfied) {
            setParamsModalAcknowledged(false)
            pendingActionRef.current = params => {
              void performRenderRef.current(params)
            }
            setLoading(false)
            setParamsModalOpen(true)
          } else if (hasRequired && !satisfied) {
            setParamsModalAcknowledged(false)
            pendingActionRef.current = params => {
              void performRenderRef.current(params)
            }
            setLoading(false)
            setParamsModalOpen(true)
          }
        }
      } catch (e: unknown) {
        if (!cancelled) toastApiError(toast, e, 'Failed to load template')
      } finally {
        if (!cancelled) setBootstrapLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- bootstrap only on template id; performRender used in pending callbacks after Apply
  }, [id, toast])

  function triggerDownload(blob: Blob, filename: string) {
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    a.click()
    URL.revokeObjectURL(url)
  }

  const fileBase = `${(templateName || 'report').replace(/\s+/g, '_')}_${new Date().toISOString().slice(0, 10)}`

  function handleParametersApply(next: Record<string, string>) {
    setParameters(next)
    setParamsModalAcknowledged(true)
    const pending = pendingActionRef.current
    pendingActionRef.current = null
    if (pending) pending(next)
  }

  async function exportPdf() {
    if (!id || !canExportPdf) return
    runAfterParametersOrNow(async params => {
      setExportingPdf(true)
      try {
        const blob = await templateApi.exportPdf(id, {
          ...buildRenderPayload(params),
          pdfOptions: {
            format: 'A4',
            printBackground: true,
            marginTop: '20mm',
            marginBottom: '20mm',
            marginLeft: '15mm',
            marginRight: '15mm',
            footerTemplate: `<div style="font-size:9px;color:#888;width:100%;padding:0 15mm;display:flex;justify-content:space-between;"><span>${templateName}</span><span><span class="pageNumber"></span>/<span class="totalPages"></span></span></div>`,
          },
        })
        triggerDownload(blob, `${fileBase}.pdf`)
        toast('PDF downloaded')
      } catch (e: unknown) {
        toastApiError(toast, e, 'PDF export failed')
      } finally {
        setExportingPdf(false)
      }
    })
  }

  async function exportExcel() {
    if (!id || !canExportExcel) return
    runAfterParametersOrNow(async params => {
      setExportingXlsx(true)
      try {
        const blob = await templateApi.exportExcel(id, {
          ...buildRenderPayload(params),
          sheetName: templateName || undefined,
        })
        triggerDownload(blob, `${fileBase}.xlsx`)
        toast('Excel downloaded')
      } catch (e: unknown) {
        toastApiError(toast, e, 'Excel export failed')
      } finally {
        setExportingXlsx(false)
      }
    })
  }

  function onRefresh() {
    runAfterParametersOrNow(params => performRender(params))
  }

  const waitingForParams = paramDefs.length > 0 && !paramsModalAcknowledged && !html
  const optionalOnlyParams = hasOnlyOptionalParameters(paramDefs)
  const blockViewer = bootstrapLoading || waitingForParams

  return (
    <div className="flex h-full flex-col overflow-hidden">
      {/* Header */}
      <div className="flex flex-shrink-0 items-center gap-3 border-b border-border bg-surface px-4 py-2.5">
        <button onClick={() => navigate('/templates')} className="text-muted hover:text-ink transition-colors">
          <ChevronLeft className="h-4 w-4" />
        </button>
        <h1 className="flex-1 truncate text-sm font-bold text-ink">{templateName || 'Report Viewer'}</h1>

        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            pendingActionRef.current = null
            setParamsModalOpen(true)
          }}
          className="flex items-center gap-1 text-muted"
          title="Custom parameters"
        >
          <SlidersHorizontal className="h-3 w-3" />
          Parameters
          {Object.keys(parameters).length > 0 ? (
            <span className="ml-1 rounded bg-accent/15 px-1.5 py-0 text-[10px] font-mono text-accent">
              {Object.keys(parameters).length}
            </span>
          ) : null}
        </Button>

        <Button variant="ghost" size="sm" onClick={onRefresh} disabled={loading || bootstrapLoading}>
          {loading ? <Spinner className="h-3 w-3" /> : <RefreshCw className="h-3 w-3" />}
          Refresh
        </Button>
        <Button variant="ghost" size="sm" onClick={() => navigate(`/designer/${id}`)}>
          <Pencil className="h-3 w-3" /> Edit
        </Button>

        {/* Excel export */}
        <Button
          variant="ghost"
          size="sm"
          onClick={exportExcel}
          disabled={exportingXlsx || bootstrapLoading || !canExportExcel}
          title={
            !canExportExcel && !bootstrapLoading
              ? 'Configure at least one sheet in Excel layout (designer) to enable export.'
              : undefined
          }
          className="border-green/30 text-green hover:bg-green/5"
        >
          {exportingXlsx ? <Spinner className="h-3 w-3" /> : <FileSpreadsheet className="h-3 w-3" />}
          Excel
        </Button>

        {/* PDF export */}
        <Button
          variant="green"
          size="sm"
          onClick={exportPdf}
          disabled={exportingPdf || bootstrapLoading || !canExportPdf}
          title={
            !canExportPdf && !bootstrapLoading ? 'Add HTML content in the designer to enable PDF export.' : undefined
          }
        >
          {exportingPdf ? <Spinner className="h-3 w-3" /> : <Download className="h-3 w-3" />}
          PDF
        </Button>
      </div>

      <ReportParametersModal
        open={paramsModalOpen}
        applied={parameters}
        definitions={paramDefs.length > 0 ? paramDefs : undefined}
        autoResolvedDeps={autoResolvedDeps}
        onClose={() => {
          setParamsModalOpen(false)
          pendingActionRef.current = null
        }}
        onApply={handleParametersApply}
      />

      {/* Iframe */}
      <div className="flex-1 overflow-hidden bg-white">
        {blockViewer ? (
          <div className="flex h-full flex-col items-center justify-center gap-4 bg-bg px-6 text-center">
            {bootstrapLoading ? (
              <Spinner className="h-6 w-6" />
            ) : (
              <>
                <p className="max-w-sm text-sm text-muted">
                  {optionalOnlyParams
                    ? 'This template uses optional data source parameters. Choose No value or Set value for each one before preview or export.'
                    : 'This template uses data sources with runtime parameters. Set them before preview or export.'}
                </p>
                <Button type="button" size="sm" onClick={() => setParamsModalOpen(true)}>
                  Set parameters
                </Button>
              </>
            )}
          </div>
        ) : (
          <iframe
            srcDoc={html}
            className="h-full w-full border-none"
            title="Report Preview"
            sandbox="allow-same-origin allow-scripts"
          />
        )}
      </div>
    </div>
  )
}
